/**
 * Fleet Firebase attestation and approval contracts (SCH-2134 U4a; KTD3).
 *
 * One internal endpoint, two bodies, discriminated on `kind`:
 *
 *  - **`quota_attestation`** — an operator read the New Project console and is
 *    recording how many projects the organization may still create. Nothing we
 *    can read for ourselves, and a create cohort is illegal without a fresh one
 *    (R21).
 *  - **`approval`** — an operator approves one binding's allocation decision.
 *    It carries the inventory FINGERPRINT the decision was made from, and the
 *    approval is refused unless that fingerprint still matches the evidence the
 *    binding points at. A changed fleet invalidates an approval rather than
 *    carrying it forward, which is the whole KTD3 gate.
 *
 * The fingerprint is a plain sha256 hex digest of non-secret facts
 * (`services/provisioning/firebase/inventory.ts`), so it is safe to paste into
 * a terminal, a ticket and this body.
 */

import { z } from 'zod'
import { FirebaseBindingLifecycleStateSchema } from '../../schemas/entities/mobile-app-firebase-project'

/** sha256, lowercase hex. Validated here so a truncated paste is a 400, not a silent mismatch. */
export const FleetInventoryFingerprintSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, { message: 'fingerprint must be a 64-character lowercase sha256 hex digest' })

/**
 * The decisions an operator can approve. `conflict` is absent on purpose: a
 * conflict is not a plan, it is the absence of one, and approving it would give
 * a later unit permission to act on contradictory evidence.
 */
export const FleetFirebaseApprovableDecisionSchema = z.enum(['adopt', 'move_then_adopt', 'create'])
export type FleetFirebaseApprovableDecision = z.infer<typeof FleetFirebaseApprovableDecisionSchema>

export const FleetFirebaseQuotaAttestationSchema = z.object({
  kind: z.literal('quota_attestation'),
  /** Projects the organization may still create, as the New Project console showed it. */
  remainingProjects: z.number().int().min(0).max(100_000),
  /** When the operator LOOKED, not when they posted. ISO-8601. */
  observedAt: z.string().datetime({ offset: true }),
  /**
   * The inventory pass this attestation was taken FOR, when there is one.
   *
   * Optional, because a quota is a property of the organization rather than of
   * one inventory: an operator may record what the console said before any pass
   * has run. Supplying it is what lets the create gate (U5) prove the count was
   * read for the cohort it is about to authorize rather than for some earlier
   * one (KTD3).
   */
  fingerprint: FleetInventoryFingerprintSchema.optional(),
  note: z.string().max(500).optional()
})

export const FleetFirebaseApprovalSchema = z.object({
  kind: z.literal('approval'),
  /** `MobileAppFirebaseProject.publicId`. */
  bindingPublicId: z.string().min(1).max(64),
  decision: FleetFirebaseApprovableDecisionSchema,
  fingerprint: FleetInventoryFingerprintSchema
})

export const FleetFirebaseAttestationRequestSchema = z.discriminatedUnion('kind', [
  FleetFirebaseQuotaAttestationSchema,
  FleetFirebaseApprovalSchema
])
export type FleetFirebaseAttestationRequest = z.infer<typeof FleetFirebaseAttestationRequestSchema>

export const FleetFirebaseAttestationResponseSchema = z.object({
  /** The `OperationsEvent.publicId` this call wrote (or deduplicated onto). */
  eventId: z.string(),
  /** Present for an approval only. */
  bindingPublicId: z.string().nullable(),
  /** The binding's lifecycle state AFTER the call. Null for a quota attestation. */
  lifecycleState: FirebaseBindingLifecycleStateSchema.nullable()
})
export type FleetFirebaseAttestationResponse = z.infer<typeof FleetFirebaseAttestationResponseSchema>
